import { api, apiBlob, apiUpload } from '../../../lib/api';
import { reauthHeaders } from '../../../lib/reauth';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CaseAttachmentItem,
  CaseEligibility,
  CaseListItem,
  CaseListQuery,
  CaseMemberOption,
  CaseMemberSummary,
  CaseRequest,
  CreateCaseRequest,
} from './caseTypes';

const CASES = '/api/cases';

/*
  Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
  the reader saying "none of these". A truthiness check would drop it and the server would read
  the axis as unmentioned, which is the opposite result.
*/
export function listParams(query: CaseListQuery | ListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);
  if ('caseStatus' in query && query.caseStatus !== undefined) params.set('caseStatus', query.caseStatus);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/** The case registry — Case Management. Evidence reads cases through its own `evidenceCasesApi`. */
export const casesApi = {
  list: (query: CaseListQuery, signal?: AbortSignal) =>
    api<PagedResult<CaseListItem>>(`${CASES}${listParams(query)}`, { signal }),

  /** Whether the caller is an employee — the index states it before Create refuses. */
  eligibility: (signal?: AbortSignal) => api<CaseEligibility>(`${CASES}/eligibility`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<CaseListItem>(`${CASES}/${id}`, { signal }),

  create: (request: CreateCaseRequest) => api<CaseListItem>(CASES, { method: 'POST', body: request }),

  update: (id: string, request: CaseRequest) =>
    api<CaseListItem>(`${CASES}/${id}`, { method: 'PUT', body: request }),

  setAccessLevel: (id: string, accessLevelId: string | null) =>
    api<CaseListItem>(`${CASES}/${id}/access-level`, { method: 'PUT', body: { accessLevelId } }),

  /** Password-confirmed: spends the proof `verifyPassword` issued. */
  deactivate: (id: string, reauthentication: string) =>
    api<void>(`${CASES}/${id}`, { method: 'DELETE', headers: reauthHeaders(reauthentication) }),

  activate: (id: string) => api<void>(`${CASES}/${id}/restore`, { method: 'POST' }),

  members: (id: string, signal?: AbortSignal) => api<CaseMemberSummary>(`${CASES}/${id}/members`, { signal }),

  saveMembers: (id: string, people: { userId: string; permissions: number }[]) =>
    api<CaseMemberSummary>(`${CASES}/${id}/members`, { method: 'PUT', body: { people } }),

  memberOptions: (id: string, signal?: AbortSignal) =>
    api<CaseMemberOption[]>(`${CASES}/${id}/member-options`, { signal }),
};

/**
 * The files under one record — a case, or a report. One shape, so one panel and one upload window
 * serve both. Every byte comes back by a FETCH (`apiBlob`): the token travels in a header no
 * navigation sends, and the server answers with an attachment on purpose.
 */
export interface FilesApi {
  list: (query: ListQuery, signal?: AbortSignal) => Promise<PagedResult<CaseAttachmentItem>>;
  /** ONE file per request — the server buffers it whole. `apiUpload` so the form shows real progress. */
  attach: (file: File, onProgress?: (percent: number) => void) => Promise<CaseAttachmentItem>;
  download: (attachmentId: string, signal?: AbortSignal) => Promise<{ blob: Blob; fileName: string | null }>;
  remove: (attachmentId: string) => Promise<void>;
  restore: (attachmentId: string) => Promise<void>;
}

export function filesAt(base: string): FilesApi {
  return {
    list: (query, signal) => api<PagedResult<CaseAttachmentItem>>(`${base}${listParams(query)}`, { signal }),
    attach: (file, onProgress) => {
      const form = new FormData();
      form.append('file', file);
      return apiUpload<CaseAttachmentItem>(base, form, { onProgress });
    },
    download: (attachmentId, signal) => apiBlob(`${base}/${attachmentId}`, { signal }),
    remove: (attachmentId) => api<void>(`${base}/${attachmentId}`, { method: 'DELETE' }),
    restore: (attachmentId) => api<void>(`${base}/${attachmentId}/restore`, { method: 'POST' }),
  };
}

/** A case's files — `cases.read` lists, `cases.attach` writes, `cases.download` reads the bytes. */
export const caseFilesApi = (caseId: string) => filesAt(`${CASES}/${caseId}/attachments`);
